import type { ActionFunction } from '@remix-run/node';
import { json } from '@remix-run/node';
import invariant from 'tiny-invariant';
import type {
    CreateOrUpdateInspirationMutation,
    CreateOrUpdateInspirationMutationVariables,
    MoveInspirationItemsMutation,
    MoveInspirationItemsMutationVariables,
} from 'generated/graphql';
import {
    CreateOrUpdateInspirationDocument,
    MoveInspirationItemsDocument,
} from 'generated/graphql';
import { pluralize } from '~/utils';

export const CREATE_AND_MOVE_BUILD_HUB_INSPIRATION_API_ROUTE =
    '/remix-api/app/build-hub-inspiration-create-and-move';

const DEFAULT_FAILED_MESSAGE = 'Failed to create MoodBoard and move pins.';

/** Response type matching DesignBoard pattern */
export interface CreateAndMoveMoodBoardResponse {
    boardId: number;
    destinationBoardId: number;
    title: string;
    description: string;
    isPrivate: boolean;
    sourceBoardId: number;
    pinIds: string[];
    toastMessageExtra: string;
}

/**
 * Action to create a new mood board and move pins to it.
 * Follows the same pattern as DesignBoard's CREATE_DESIGN_BOARD_AND_MOVE_ITEM.
 */
export const action: ActionFunction = async ({ request, context }) => {
    const data = await request.formData();

    // Required fields for board creation
    invariant(data.get('id'), 'No BuildHub ID submitted for creation.');

    // Required fields for pin moving
    invariant(data.get('sourceBoardId'), 'Source moodboard id required.');
    invariant(data.get('pinListString'), 'Pin ids required for move.');
    invariant(data.get('buildHubUrl'), 'Moodboard BuildHub url required.');

    const title = (data.get('title') as string) || '';
    const description = (data.get('description') as string) || '';
    const isPrivateBool = ['true', '1'].includes(
        (data.get('isPrivate') as string) || ''
    );
    const sourceBoardId = Number(data.get('sourceBoardId'));
    const pins = (data.get('pinListString') as string).split(',');
    const buildHubUrl = data.get('buildHubUrl') as string;

    // Step 1: Create the mood board
    const createVariables: CreateOrUpdateInspirationMutationVariables = {
        BuildHubID: data.get('id') as string,
        Title: title,
        Description: description,
        Private: isPrivateBool,
    };

    try {
        // Create the mood board
        const { data: createResult } = await context.client.mutate<
            CreateOrUpdateInspirationMutation,
            CreateOrUpdateInspirationMutationVariables
        >({
            mutation: CreateOrUpdateInspirationDocument,
            variables: createVariables,
        });

        const createdBoard = createResult?.projectInspirationGroupUpsert;
        if (
            !createdBoard ||
            createdBoard.__typename !== 'InspirationGroup' ||
            !createdBoard.ID
        ) {
            return json({
                message: 'Failed to create mood board.',
            });
        }

        const newBoardId = Number(createdBoard.ID);

        // Step 2: Move pins to the newly created board
        const moveVariables: MoveInspirationItemsMutationVariables = {
            FromBoardID: sourceBoardId,
            ToBoardID: newBoardId,
            BuildHubUrl: buildHubUrl,
            Pins: pins,
        };

        const { data: moveResult } = await context.client.mutate<
            MoveInspirationItemsMutation,
            MoveInspirationItemsMutationVariables
        >({
            mutation: MoveInspirationItemsDocument,
            variables: moveVariables,
        });

        if (!moveResult?.moodBoardPinsMove) {
            return json({
                message: 'Board created but failed to move pins.',
                boardId: newBoardId,
            });
        }

        // Return response matching DesignBoard pattern
        const pinString = pluralize(pins.length, 'pin');
        const toastMessageExtra = `Selected ${pinString} have been moved to this board.`;

        const response: CreateAndMoveMoodBoardResponse = {
            boardId: sourceBoardId,
            destinationBoardId: newBoardId,
            title,
            description,
            isPrivate: isPrivateBool,
            sourceBoardId,
            pinIds: pins,
            toastMessageExtra,
        };

        return json(response);
    } catch (error) {
        return json({
            message:
                error instanceof Error ? error.message : DEFAULT_FAILED_MESSAGE,
        });
    }
};
